import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Landmark } from "lucide-react";
import { api, FIATS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { BANKS } from "@/constants/testIds";

export default function BankAccounts() {
  const { t } = useI18n();
  const [banks, setBanks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    bank_name: "",
    account_holder: "",
    iban_or_account: "",
    currency: "USD",
    country: "US",
  });

  const load = useCallback(async () => {
    const r = await api.get("/bank-accounts");
    setBanks(r.data.bank_accounts);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bank-accounts", form);
      toast.success(t("banks.bank_linked"));
      setShowForm(false);
      setForm({ bank_name: "", account_holder: "", iban_or_account: "", currency: "USD", country: "US" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("banks.link_failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t("banks.remove_confirm"))) return;
    try {
      await api.delete(`/bank-accounts/${id}`);
      toast.success(t("banks.bank_removed"));
      await load();
    } catch {
      toast.error(t("banks.remove_failed"));
    }
  };

  const inputCls = "w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("banks.eyebrow")}</div>
          <h1 className="font-display text-3xl font-bold mt-1">{t("banks.title")}</h1>
        </div>
        <button
          data-testid={BANKS.addButton}
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] transition-colors text-sm"
        >
          <Plus size={14} /> {showForm ? t("banks.cancel") : t("banks.add")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#2A2E38] bg-[#141720] p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t("banks.bank_name")}</label>
            <input data-testid={BANKS.bankNameInput} required minLength={2} className={`${inputCls} mt-2`} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder={t("banks.bank_name_ph")} />
          </div>
          <div>
            <label className={labelCls}>{t("banks.holder")}</label>
            <input data-testid={BANKS.holderInput} required minLength={2} className={`${inputCls} mt-2`} value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder={t("banks.holder_ph")} />
          </div>
          <div>
            <label className={labelCls}>{t("banks.iban")}</label>
            <input data-testid={BANKS.ibanInput} required minLength={6} className={`${inputCls} mt-2 font-mono`} value={form.iban_or_account} onChange={(e) => setForm({ ...form, iban_or_account: e.target.value })} placeholder={t("banks.iban_ph")} />
          </div>
          <div>
            <label className={labelCls}>{t("banks.currency")}</label>
            <select data-testid={BANKS.currencySelect} className={`${inputCls} mt-2`} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              {FIATS.map((f) => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t("banks.country")}</label>
            <input data-testid={BANKS.countryInput} required maxLength={2} minLength={2} className={`${inputCls} mt-2 font-mono uppercase`} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} placeholder={t("banks.country_ph")} />
          </div>
          <div className="md:col-span-2">
            <button data-testid={BANKS.submitButton} type="submit" disabled={busy} className="px-6 py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-50">
              {busy ? t("banks.linking") : t("banks.link_account")}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.map((b) => (
          <div key={b.id} className="border border-[#2A2E38] bg-[#141720] p-6 relative group">
            <div className="flex items-start justify-between">
              <Landmark size={20} className="text-[#7C5CFF]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#7C9C69]">{t("banks.verified")}</span>
            </div>
            <h3 className="font-display text-xl font-bold mt-4">{b.bank_name}</h3>
            <div className="mt-1 text-sm text-[#A3A9B8]">{b.account_holder}</div>
            <div className="mt-4 font-mono text-xs text-[#6E7587]">
              ••••••••{b.last4} · {b.currency} · {b.country}
            </div>
            <button
              onClick={() => remove(b.id)}
              className="absolute top-4 right-4 p-2 text-[#6E7587] hover:text-[#C75C5C] opacity-0 group-hover:opacity-100 transition-opacity"
              title={t("banks.cancel")}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {banks.length === 0 && !showForm && (
          <div className="md:col-span-3 border border-dashed border-[#2A2E38] p-10 text-center text-[#6E7587]">
            {t("banks.no_accounts")}
          </div>
        )}
      </div>
    </div>
  );
}
